import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { type CSSProperties } from 'styled-components';

import { Link } from '@sorare/routing';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { ANY_SPORT_MARKET_COMMON_PACKS_GROUP } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { isSpecialCardSet } from '@sorare/core/src/lib/cardSets';

import { usePackAssets } from 'components/commonCard/PacksTypePage/usePackAssets';

import {
  Desktop,
  Grid,
  Header,
  Logo,
  MainPack,
  NeonEdge,
  PackImg,
  PacksWrapper,
  PlayersImg,
  SecondaryPack,
  SeriesCardPackGroupWrapper,
} from './ui';
import { PackWithBuyButton } from '../PackWithBuyButton';
import type { SeriesCardPackGroup_cardPackGroup } from './__generated__/index.graphql';
import bundesligaPackGroup from './assets/bundesliga-pack-group.webp';
import laligaPackGroup from './assets/laliga-pack-group.webp';
import ligue1PackGroup from './assets/ligue1-pack-group.webp';
import players from './assets/players.webp';
import premierleaguePackGroup from './assets/premierleague-pack-group.webp';

const getBgSeriesImage = (cardPackGroupId: string) => {
  if (!cardPackGroupId) return null;
  if (cardPackGroupId.includes('premier-league')) return premierleaguePackGroup;
  if (cardPackGroupId.includes('bundesliga')) return bundesligaPackGroup;
  if (cardPackGroupId.includes('laliga')) return laligaPackGroup;
  if (cardPackGroupId.includes('ligue-1')) return ligue1PackGroup;
  return null;
};

type Pack = SeriesCardPackGroup_cardPackGroup['cardPacks'][number] | null;

type Result = {
  goat?: Pack;
  elite?: Pack;
  basic?: Pack;
};

type Props = {
  cardPackGroup: SeriesCardPackGroup_cardPackGroup;
  onClickBuy: (cardPackGroupId: string, cardPackSlug: string) => void;
  wide?: boolean;
};

export const SeriesCardPackGroup = ({
  cardPackGroup,
  onClickBuy,
  wide,
}: Props) => {
  const isLaptopOrAbove = useIsLaptop();
  const { goat, elite, basic } = cardPackGroup.cardPacks.reduce<Result>(
    (acc, pack) => {
      const { slug } = pack;
      if (slug.includes('goat') || slug.includes('icon')) {
        return { ...acc, goat: pack };
      }
      if (slug.includes('elite') || slug.includes('star')) {
        return { ...acc, elite: pack };
      }
      if (slug.includes('basic')) {
        return { ...acc, basic: pack };
      }
      return acc;
    },
    { goat: null, elite: null, basic: null }
  );

  const { bgImage } = usePackAssets({
    cardPackGroupId: idFromObject(cardPackGroup.id),
  });
  const { generateSportContextPath } = useSportContext();
  const isSeries = isSpecialCardSet(cardPackGroup?.cardSet?.slug);

  if (!isSeries) return null;

  const bgSeriesImage = getBgSeriesImage(idFromObject(cardPackGroup.id));

  const backgroundImage = bgSeriesImage || bgImage;

  const color = cardPackGroup.cardPacks.find(
    pack => pack.primaryColor
  )?.primaryColor;

  return (
    <ConditionalWrapper
      wrap={!!isLaptopOrAbove}
      Wrapper={Bezel}
      props={{
        bezelSize: 24,
        radius: 16,
        background: `linear-gradient(
        180deg,
        rgba(14, 14, 14, 0.5) 0%,
        rgba(14, 14, 14, 0.9) 79.85%,
        #0e0e0e 99.69%
      ),
     url(${backgroundImage}) top left / cover no-repeat`,
      }}
    >
      <SeriesCardPackGroupWrapper
        as={LinkBox}
        style={
          {
            '--background-image': `url(${backgroundImage})`,
          } as CSSProperties
        }
        className={classNames({ wide })}
      >
        <NeonEdge side="top" showNeon={'always'} color={color || ''}>
          <Header>
            <LinkOverlay
              as={isLaptopOrAbove ? Link : 'div'}
              to={`${generateSportContextPath(
                ANY_SPORT_MARKET_COMMON_PACKS_GROUP,
                {
                  params: {
                    group: idFromObject(cardPackGroup.id),
                  },
                }
              )}?pack=${goat?.slug || elite?.slug || basic?.slug}`}
            >
              <Logo src={cardPackGroup?.pictureUrl} alt={cardPackGroup.title} />
            </LinkOverlay>
          </Header>
          <Grid>
            {!!goat && (
              <MainPack>
                <Vertical gap={0.5}>
                  <BrandM>
                    <FormattedMessage
                      id="SeriesCardPackGroup.title"
                      defaultMessage="Star Pack"
                    />
                  </BrandM>
                  <LabelM>
                    <FormattedMessage
                      id="SeriesCardPackGroup.description"
                      defaultMessage="One Guaranteed Star Player!"
                    />
                  </LabelM>
                </Vertical>
                <PackWithBuyButton
                  cardPack={goat}
                  cardPackGroupId={cardPackGroup.id}
                  onClickBuy={onClickBuy}
                  withEligiblePlayers
                  gap={3}
                  isPremiumPack
                />
              </MainPack>
            )}
            {!!basic && (
              <SecondaryPack>
                <PackWithBuyButton
                  cardPack={basic}
                  cardPackGroupId={cardPackGroup.id}
                  onClickBuy={onClickBuy}
                  gap={3}
                />
              </SecondaryPack>
            )}
            {!!elite && (
              <SecondaryPack>
                <PackWithBuyButton
                  cardPack={elite}
                  cardPackGroupId={cardPackGroup.id}
                  onClickBuy={onClickBuy}
                  gap={3}
                />
              </SecondaryPack>
            )}
          </Grid>
          <Desktop>
            <PlayersImg src={players} alt="players" />
            <PacksWrapper>
              {!!elite && (
                <PackImg
                  pictureUrl={proxyUrl(elite?.pictureUrl, { cropWidth: 160 })}
                  alt="star"
                />
              )}
              {!!goat && (
                <PackImg
                  pictureUrl={proxyUrl(goat?.pictureUrl, { cropWidth: 160 })}
                  alt="icon"
                />
              )}
              {!!basic && (
                <PackImg
                  pictureUrl={proxyUrl(basic?.pictureUrl, { cropWidth: 160 })}
                  alt="basic"
                />
              )}
            </PacksWrapper>
          </Desktop>
        </NeonEdge>
      </SeriesCardPackGroupWrapper>
    </ConditionalWrapper>
  );
};

SeriesCardPackGroup.fragments = {
  cardPackGroup: gql`
    fragment SeriesCardPackGroup_cardPackGroup on CardPackGroup {
      id
      title
      pictureUrl
      cardSet {
        slug
      }
      cardPacks {
        primaryColor: color(variant: PRIMARY)
        slug
        eligibleCommonPlayers {
          id
          pictureUrl
        }
        ...PackWithBuyButton_cardPack
      }
    }
    ${PackWithBuyButton.fragments.cardPack}
  ` as TypedDocumentNode<SeriesCardPackGroup_cardPackGroup>,
};
